// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Accordion from "@insite/mobius/Accordion";
import { AccordionSectionPanel } from "@insite/mobius/AccordionSection";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { getProp } from "@insite/mobius/utilities";
import TitleTypography, { TitleTypographyStyles } from "@keller/App/Components/TitleTypography/TitleTypography";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@keller/MobiusOverrides/AccordionSection";
import { color, mergeWithCss, xScreen } from "@keller/XDL/Utilities";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    link?: string | LinkFieldValue;
    linkText?: string;
    title?: string;
    extendedStyles?: HomeAccordionStyles;
}

export interface HomeAccordionStyles {
    managerAccordionSection?: AccordionSectionPresentationProps;
    titleTypography?: TitleTypographyStyles;
    bodyGridContainer?: GridContainerProps;
}

const accordionStyle = css`
    &:after {
        font-size: 14px;
        font-weight: bold;
        letter-spacing: 0;
        line-height: 16px;
        text-align: center;
        margin-left: 12px;
        white-space: nowrap;
    }
    ${xScreen("lte", "xs")} {
        &:after {
            display: none;
        }
    }
    &:hover {
        &:after {
            color: ${color("common.ui1")};
        }
    }
`;

const homeAccordionStyles: HomeAccordionStyles = {
    managerAccordionSection: {
        mergeCss: true,
        toggleIconProps: {
            mergeCss: true,
            css: css`
                width: auto;
                ${getProp("theme.accordion.sectionDefaultProps.toggleIconProps.css")};
                &:after {
                    content: "Expand";
                }
                ${accordionStyle}
            `,
        },
        collapseIconProps: {
            mergeCss: true,
            css: css`
                width: auto;
                ${getProp("theme.accordion.sectionDefaultProps.toggleIconProps.css")};
                &:after {
                    content: "Collapse";
                }
                ${accordionStyle}
            `,
        },

        headerProps: {
            css: css`
                background: transparent;
                margin-bottom: 24px;

                button,
                button:focus,
                ${AccordionSectionPanel} + & button:focus {
                    padding: 0;
                    border: 0;
                }
            `,
        },
        panelProps: {
            css: css`
                background: transparent;
                padding: 0;
                border: 0;
            `,
        },
    },
    titleTypography: {
        wrapper: {
            css: css`
                margin-bottom: 0;
            `,
        },
    },
    bodyGridContainer: {
        gap: 0,
    },
};

const HomeAccordion: FC<React.PropsWithChildren<OwnProps>> = ({ children, link, linkText, title, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(homeAccordionStyles, extendedStyles));

    return (
        <Accordion headingLevel={2}>
            <ManagedAccordionSection
                title={
                    <TitleTypography
                        extendedStyles={styles.titleTypography}
                        link={link}
                        linkText={linkText || translate("View All")}
                    >
                        {title}
                    </TitleTypography>
                }
                initialExpanded={true}
                hasLinkInsideTitle={true}
                {...styles.managerAccordionSection}
            >
                <GridContainer {...styles.bodyGridContainer}>{children}</GridContainer>
            </ManagedAccordionSection>
        </Accordion>
    );
};

export default HomeAccordion;
